import { IMenuItem, TSideBarGroups } from '@components/common/SideBar/interfaces';

import { TGroupMenuTitles, TVendorsValues } from '@enums/common';

interface IToSideBarMenuItems {
	sideBarItems: IMenuItem[];
	sideBarGroups: Partial<TSideBarGroups>;
	currentVendor?: TVendorsValues | undefined;
	allVendors?: TVendorsValues[];
}

type IMenuItemWithOrder = Omit<IMenuItem, 'order'> & { order: number };

export const toSideBarMenuItems = ({
	sideBarItems,
	sideBarGroups,
	currentVendor,
	allVendors,
}: IToSideBarMenuItems): IMenuItem[] => {
	const vendorSpecificGroupKeys = Object.entries(sideBarGroups).reduce<string[]>((acc, [key, value]) => {
		if (value?.isVendorSpecific) {
			acc.push(key);
		}

		return acc;
	}, []);

	const displayableParentGroupKeys = vendorSpecificGroupKeys.reduce<string[]>((acc, targetGroupKey) => {
		const groupChildItems = sideBarItems
			.filter(sideBarItem => sideBarItem.parent === targetGroupKey && !sideBarItem.isHidden);
		const hasItemWithMatchingVendor = groupChildItems.some(i => i?.vendors?.some(v => allVendors?.includes(v)));

		if (hasItemWithMatchingVendor) {
			acc.push(targetGroupKey);
		}

		return acc;
	}, []);

	const menuItems = sideBarItems
		.reduce((acc, curr) => {
			const parentGroupKey: keyof TSideBarGroups = curr.parent as any;
			const isVendorSpecific = sideBarGroups[parentGroupKey]?.isVendorSpecific;

			const isCurrentItemDisplayed = (isVendorSpecific &&
				curr?.vendors?.length &&
				(currentVendor && curr.vendors.includes(currentVendor) || !currentVendor)
			);

			const initialAccumulator = Object.keys(sideBarGroups).reduce(
				(acc, groupKey) => {
					acc[groupKey as TGroupMenuTitles] = {
						...sideBarGroups[groupKey as TGroupMenuTitles]!,
						groupItems: [],
					} as unknown as IMenuItem;

					return acc;
				},
				{} as Record<TGroupMenuTitles, IMenuItem>,
			);

			const itemsMap = sideBarItems.reduce((acc, currentItem) => {
				if (currentItem.isHidden) {
					return acc;
				}

				if (currentItem.vendors?.length && !currentItem.vendors.includes(currentVendor!)) {
					return acc;
				}

				if (currentItem.parent) {
					const parentKey = currentItem.parent as TGroupMenuTitles;
					acc[parentKey].groupItems!.push(currentItem);
				} else {
					const itemKey = currentItem.title as TGroupMenuTitles;
					acc[itemKey] = currentItem;
				}
				return acc;
			}, initialAccumulator);

			if (curr.isHidden || (!isCurrentItemDisplayed && isVendorSpecific)) {
				return acc;
			}

			return Object.entries(itemsMap).reduce<any>((acc, [menuKey, menuValue]) => {
				if (
					!menuValue?.groupItems?.length &&
					!displayableParentGroupKeys.includes(menuKey as TGroupMenuTitles)
				) {
					return acc;
				}

				acc[menuKey] = menuValue;

				return acc;
			}, {});
		}, {} as Record<TGroupMenuTitles, IMenuItemWithOrder>);

	return Object.values(menuItems).sort((a, b) => a.order - b.order);
};
